/** What the host page asks for (`map` attribute): `auto` hides the map on a narrow widget. */
export type MapPreset = 'shown' | 'hidden' | 'auto';

/** Up to this width the widget stacks chat over map (styles.css) — there a visible map leaves the
 *  chat too little room, so `auto` starts with the map hidden. */
export const NARROW_MAX_WIDTH = 760;

const PREFERENCE_KEY = 'nds-chat-map:map';

export function parsePreset(value: string | null, fallback: MapPreset): MapPreset {
  return value === 'shown' || value === 'hidden' || value === 'auto' ? value : fallback;
}

/** The visitor's choice: made on this page view (`now`), or remembered from an earlier one. */
export interface VisitorChoice {
  now: boolean | null;
  remembered: boolean | null;
}

/** Whether the map is shown, given the host's preset, whether the visitor has the toggle, their
 *  choice and the widget's width. In this order:
 *  1. Without the toggle nobody could open a hidden map: only an explicit `hidden` hides it, no choice counts.
 *  2. A choice made on this page view wins — the visitor just pressed the button.
 *  3. An explicit `shown`/`hidden` of the host wins over a remembered choice: the choice is kept per
 *     site (localStorage), and a host page that says `hidden` for its narrow sidebar knows its layout
 *     better than a choice made on another page of the site.
 *  4. Under `auto` the remembered choice counts, then the width. */
export function resolveMapShown(preset: MapPreset, toggle: boolean, choice: VisitorChoice, width: number): boolean {
  if (!toggle) return preset !== 'hidden';
  if (choice.now !== null) return choice.now;
  if (preset !== 'auto') return preset === 'shown';
  if (choice.remembered !== null) return choice.remembered;
  return width > NARROW_MAX_WIDTH;
}

/** The visitor's last choice, if storage is available and holds one. */
export function rememberedChoice(): boolean | null {
  try {
    const value = localStorage.getItem(PREFERENCE_KEY);
    return value === 'shown' ? true : value === 'hidden' ? false : null;
  } catch {
    return null;
  }
}

export function rememberChoice(shown: boolean): void {
  try {
    localStorage.setItem(PREFERENCE_KEY, shown ? 'shown' : 'hidden');
  } catch {
    /* without storage the choice holds for this page view */
  }
}

// the folded map of Material Icons (Apache 2.0); "off" crosses it out
const MAP_PATH = 'M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z';
const MAP_ICON = `<svg class="icon-map" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="${MAP_PATH}" fill="currentColor"/></svg>`;
const MAP_OFF_ICON = `<svg class="icon-map-off" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="${MAP_PATH}" fill="currentColor" opacity=".55"/><path d="M3 3l18 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>`;

/**
 * The button in the chat head that hides and shows the map. Its symbol shows the state (the map, or
 * the map crossed out), also where only "Karte" fits. While the map is hidden and an answer with map
 * content arrives, the button carries a dot (read as "neu") and a polite announcement says so — the
 * map does not open by itself.
 */
export class MapToggle {
  readonly button: HTMLButtonElement;
  /** polite live region, outside the button so its label changes are not read twice */
  readonly status: HTMLElement;
  private readonly verb: HTMLElement;
  /** the "neu" dot */
  private readonly fresh: HTMLElement;

  /** `controls`: the id of the map pane; `statusId`: an id for the announcement, unique in the shadow root */
  constructor(controls: string, statusId: string, onToggle: () => void) {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'map-toggle';
    this.button.setAttribute('aria-controls', controls);
    // The name comes from the content, so every visible word is in it (WCAG 2.5.3): „Karte ausblenden“,
    // „Karte einblenden“, with the dot „Karte einblenden neu“. "Karte" is always visible, the verb
    // only where there is room (styles.css), but it is always read. The dot sits on the button's
    // corner, so it does not make the button wider.
    this.button.innerHTML = `${MAP_ICON}${MAP_OFF_ICON}<span class="map-toggle-text">Karte<span class="map-toggle-verb"></span></span> <span class="map-toggle-new" hidden><span class="visually-hidden">neu</span></span>`;
    this.verb = this.button.querySelector('.map-toggle-verb')!;
    this.fresh = this.button.querySelector('.map-toggle-new')!;
    this.button.addEventListener('click', onToggle);
    this.status = document.createElement('p');
    this.status.id = statusId;
    this.status.className = 'visually-hidden';
    this.status.setAttribute('role', 'status');
  }

  setExpanded(shown: boolean): void {
    // called on every resize: rewriting an unchanged name would make a screen reader read it again
    if (this.button.getAttribute('aria-expanded') === String(shown)) return;
    this.button.setAttribute('aria-expanded', String(shown));
    this.verb.textContent = shown ? ' ausblenden' : ' einblenden';
  }

  /** Mark (or clear) with the dot that the hidden map has something new to show. */
  setFresh(fresh: boolean): void {
    if (fresh === !this.fresh.hidden) return;
    this.fresh.hidden = !fresh;
    this.status.textContent = fresh ? 'Neue Kartendaten zur Antwort – „Karte einblenden“ zeigt sie.' : '';
    // the mark's meaning, read with the button
    if (fresh) this.button.setAttribute('aria-describedby', this.status.id);
    else this.button.removeAttribute('aria-describedby');
  }

  setAvailable(available: boolean): void {
    this.button.hidden = !available;
  }
}
